// =============================================================================
// components/QuickCreateOrderDialog.js — Yeni Sipariş Hızlı Oluştur (Faz BE)
// =============================================================================
// Telefonla / depoda alınan sipariş için. Müşteri seç (autocomplete),
// ürün+adet ekle (autocomplete + qty), opsiyonel fiyat override + not,
// kaydet → /api/admin/orders/quick-create.
//
// Quick-search endpoint kullanılır (Faz AY) — zaten paralel customers + products.
// =============================================================================

import React, { useState, useEffect, useRef } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Autocomplete, Box, Typography, IconButton, Stack, CircularProgress, Alert,
  Table, TableBody, TableCell, TableHead, TableRow, Divider
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import toast from "react-hot-toast";

const API_BASE = "https://api.miksanplastik.com.tr";

// Debounced search hook for quick-search endpoint
function useQuickSearch(query, kind) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.length < 2) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const r = await fetch(`${API_BASE}/api/admin/quick-search?q=${encodeURIComponent(query)}&limit=10`,
                              { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) {
          const d = await r.json();
          setResults(d[kind] || []);
        }
      } catch (_) { /* silent */ }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, kind]);

  return { results, loading };
}

export default function QuickCreateOrderDialog({ open, onClose, onCreated, prefillOrderId = null }) {
  // Müşteri
  const [customer, setCustomer] = useState(null);
  const [customerInput, setCustomerInput] = useState("");
  const { results: customerResults, loading: customerLoading } = useQuickSearch(customerInput, "customers");

  // Ürünler (cart)
  const [items, setItems] = useState([]);  // [{ product, quantity, unit_price }]

  // Faz BK: prefill state — başka bir siparişten kopyalama
  const [prefilling, setPrefilling] = useState(false);
  const [prefillError, setPrefillError] = useState(null);

  // Prefill: dialog açıldı + prefillOrderId verildi → eski siparişi çek, customer + items doldur
  useEffect(() => {
    if (!open || !prefillOrderId) return;
    setPrefilling(true); setPrefillError(null);
    const token = localStorage.getItem("token");
    fetch(`${API_BASE}/api/orders/${prefillOrderId}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : Promise.reject(new Error('Sipariş yüklenemedi')))
      .then(order => {
        // Customer object — quick-search formatına benzer
        setCustomer({
          id: order.user_id,
          name: order.user_name,
          company_name: order.user_company,
          email: order.user_email,
          phone: order.user_phone,
          tax_number: order.tax_no
        });
        // Items — her item için product objesi sentezle (stok bilinmediği için "?" ile)
        setItems((order.items || []).map(it => ({
          product: {
            id: it.product_id,
            name: it.name,
            sku: '',
            stock: null,  // null → display "?"
            unit: it.unit,
            image_url: it.image_url,
            price: Number(it.unit_price)
          },
          quantity: Number(it.quantity),
          unit_price: Number(it.unit_price)
        })));
        setNote(`#${prefillOrderId} kopyası`);
      })
      .catch(e => setPrefillError(e.message))
      .finally(() => setPrefilling(false));
  }, [open, prefillOrderId]);

  // Yeni satır ekleme
  const [productInput, setProductInput] = useState("");
  const [pendingProduct, setPendingProduct] = useState(null);
  const [pendingQty, setPendingQty] = useState(1);
  const { results: productResults, loading: productLoading } = useQuickSearch(productInput, "products");

  // Sipariş notu
  const [note, setNote] = useState("");

  // Submit
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setCustomer(null); setCustomerInput("");
    setItems([]);
    setProductInput(""); setPendingProduct(null); setPendingQty(1);
    setNote("");
    setPrefillError(null);  // Faz BK
  };

  const handleClose = () => { if (!busy) { reset(); onClose(); } };

  const addItem = () => {
    if (!pendingProduct) { toast.error("Önce ürün seç"); return; }
    if (pendingQty <= 0) { toast.error("Adet > 0 olmalı"); return; }
    // Aynı ürün varsa adet topla
    const existing = items.find(it => it.product.id === pendingProduct.id);
    if (existing) {
      setItems(prev => prev.map(it => it.product.id === pendingProduct.id
        ? { ...it, quantity: it.quantity + pendingQty } : it));
    } else {
      setItems(prev => [...prev, {
        product: pendingProduct,
        quantity: pendingQty,
        unit_price: Number(pendingProduct.price)
      }]);
    }
    setPendingProduct(null); setPendingQty(1); setProductInput("");
  };

  const removeItem = (productId) => setItems(prev => prev.filter(it => it.product.id !== productId));

  const updateItemPrice = (productId, newPrice) => {
    const p = parseFloat(newPrice);
    if (isNaN(p) || p < 0) return;
    setItems(prev => prev.map(it => it.product.id === productId ? { ...it, unit_price: p } : it));
  };
  const updateItemQty = (productId, newQty) => {
    const q = parseInt(newQty, 10);
    if (isNaN(q) || q <= 0) return;
    setItems(prev => prev.map(it => it.product.id === productId ? { ...it, quantity: q } : it));
  };

  const total = items.reduce((s, it) => s + (it.quantity * it.unit_price), 0);

  const submit = async () => {
    if (!customer) { toast.error("Müşteri seç"); return; }
    if (items.length === 0) { toast.error("En az 1 ürün ekle"); return; }
    setBusy(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/api/admin/orders/quick-create`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          user_id: customer.id,
          items: items.map(it => ({
            product_id: it.product.id,
            quantity: it.quantity,
            unit_price: it.unit_price
          })),
          note
        })
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error("Sipariş oluşturma hatası: " + (data.error || res.status));
        setBusy(false);
        return;
      }
      toast.success(`Sipariş #${data.order_id} oluşturuldu — Onay Bekliyor tab'ında`, { duration: 5000 });
      reset();
      setBusy(false);
      onClose();
      if (onCreated) onCreated(data);
    } catch (e) {
      toast.error("Hata: " + e.message);
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {prefillOrderId ? `↻ Siparişi Kopyala (#${prefillOrderId} → yeni sipariş)` : '+ Yeni Sipariş Oluştur (telefonla / yüz yüze)'}
      </DialogTitle>
      <DialogContent dividers>
        {prefilling && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CircularProgress size={16} />
          <Typography variant="caption">Eski sipariş yükleniyor...</Typography>
        </Box>}
        {prefillError && <Alert severity="error" sx={{ mb: 2 }}>Kopyalama hatası: {prefillError}</Alert>}
        <Stack spacing={2}>
          {/* Müşteri Autocomplete */}
          <Autocomplete
            value={customer}
            onChange={(_, v) => setCustomer(v)}
            inputValue={customerInput}
            onInputChange={(_, v) => setCustomerInput(v)}
            options={customerResults}
            getOptionLabel={(o) => o ? `${o.company_name || o.name} ${o.email ? '· ' + o.email : ''}` : ''}
            isOptionEqualToValue={(o, v) => o?.id === v?.id}
            loading={customerLoading}
            renderInput={(params) => (
              <TextField {...params} label="Müşteri" placeholder="Ad / şirket / email / telefon ara..." size="small" required
                         InputProps={{ ...params.InputProps,
                           endAdornment: (<>{customerLoading ? <CircularProgress size={14} /> : null}{params.InputProps.endAdornment}</>) }} />
            )}
          />

          {customer && (
            <Alert severity="info" sx={{ py: 0 }}>
              <strong>{customer.company_name || customer.name}</strong>
              {customer.tax_number ? ` · VKN ${customer.tax_number}` : ''}
              {customer.phone ? ` · 📞 ${customer.phone}` : ''}
            </Alert>
          )}

          <Divider sx={{ my: 1 }} />

          {/* Ürün Ekleme Satırı */}
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Autocomplete
              sx={{ flex: 1 }}
              value={pendingProduct}
              onChange={(_, v) => { setPendingProduct(v); if (v) setPendingQty(1); }}
              inputValue={productInput}
              onInputChange={(_, v) => setProductInput(v)}
              options={productResults}
              getOptionLabel={(o) => o ? `${o.name} ${o.sku ? '· ' + o.sku : ''} (Stok: ${o.stock})` : ''}
              isOptionEqualToValue={(o, v) => o?.id === v?.id}
              loading={productLoading}
              renderInput={(params) => (
                <TextField {...params} label="Ürün ara" placeholder="Ad / SKU / barcode..." size="small"
                           InputProps={{ ...params.InputProps,
                             endAdornment: (<>{productLoading ? <CircularProgress size={14} /> : null}{params.InputProps.endAdornment}</>) }} />
              )}
            />
            <TextField
              type="number" label="Adet" size="small"
              value={pendingQty}
              onChange={(e) => setPendingQty(parseInt(e.target.value, 10) || 1)}
              inputProps={{ min: 1 }}
              sx={{ width: 100 }}
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={addItem} disabled={!pendingProduct}>
              Ekle
            </Button>
          </Stack>

          {/* Cart tablosu */}
          {items.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Ürün</TableCell>
                  <TableCell align="right" sx={{ width: 90 }}>Adet</TableCell>
                  <TableCell align="right" sx={{ width: 110 }}>Birim ₺</TableCell>
                  <TableCell align="right" sx={{ width: 110 }}>Toplam</TableCell>
                  <TableCell sx={{ width: 40 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map(it => (
                  <TableRow key={it.product.id}>
                    <TableCell>
                      <Typography fontSize={13}>{it.product.name}</Typography>
                      {it.product.sku && <Typography variant="caption" color="text.secondary">{it.product.sku}</Typography>}
                    </TableCell>
                    <TableCell align="right">
                      <TextField type="number" size="small" value={it.quantity}
                                 onChange={(e) => updateItemQty(it.product.id, e.target.value)}
                                 inputProps={{ min: 1, style: { textAlign: 'right', padding: '4px 6px' } }}
                                 sx={{ width: 70 }} />
                    </TableCell>
                    <TableCell align="right">
                      <TextField type="number" size="small" value={it.unit_price}
                                 onChange={(e) => updateItemPrice(it.product.id, e.target.value)}
                                 inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right', padding: '4px 6px' } }}
                                 sx={{ width: 90 }} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      ₺{(it.quantity * it.unit_price).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" color="error" onClick={() => removeItem(it.product.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={3} align="right" sx={{ fontWeight: 700 }}>TOPLAM</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, fontSize: 16, color: '#1565C0' }}>
                    ₺{total.toFixed(2)}
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}

          <TextField
            label="Sipariş Notu (opsiyonel)" multiline rows={2} size="small"
            value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Acil sevk, özel ambalaj vs."
          />

          <Alert severity="warning" sx={{ py: 0 }}>
            Sipariş "Onay Bekliyor" tab'ında çıkar. Onayla butonu ile BH'ye TASLAK gider.
            Stok rezerve edilir (yetersizse uyarı verir).
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>Vazgeç</Button>
        <Button variant="contained" color="success" onClick={submit}
                disabled={busy || !customer || items.length === 0}
                startIcon={busy ? <CircularProgress size={16} /> : null}>
          {busy ? 'Oluşturuluyor...' : `Sipariş Oluştur (${items.length} ürün, ₺${total.toFixed(2)})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
